/* eslint-disable prettier/prettier */
import React, { useCallback } from 'react';

import GroupBadges from 'components/Badges/GroupBadges';
import Checkbox from 'components/Checkbox';
import RadioButton from 'components/RadioButton';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import { SpecificList as SpecificProps } from 'models/specificList';
import { getFormattedPrice } from 'utils/currency';

import {
  Arrow,
  Container,
  Row,
  Detail,
  Name,
  SubItem,
  InfoContainer,
  Badge,
  BadgeContainer,
  ContentContainer,
  NameContainer,
  ImageContainer,
  AllianceImageContainer,
  AllianceImageContent,
  EntityTypeImageContainer,
  TrashCan,
  Specific,
  StarOutlined,
} from './styles';
import Tooltip from 'components/Tooltips/Tooltip';

interface Props {
  lineGroup?: boolean;
  children?: JSX.Element;
  subItems?: boolean;
  item: GroupedListModel;
  isDisabled?: boolean;
  isChecked?: boolean;
  selectableType?: 'arrow' | 'check' | 'radio' | 'trashcan' | null;
  onSelect: (user: any) => void;
  isFavorite?: boolean;
}

const Item = ({ item, isChecked, isDisabled, lineGroup, selectableType, onSelect, subItems, isFavorite }: Props) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency: item.currency, t });
    return formatted;
  };

  const renderSelectType = useCallback(() => {
    switch (selectableType) {
      case 'arrow':
        return <Arrow />;
      case 'check':
        return <Checkbox circle checked={isChecked || false} disabled={isDisabled} />;
      case 'trashcan':
        return <TrashCan src="/corporate/images/trash.svg" />;
      case 'radio':
        return <RadioButton value={item.id} optionValue={item.name} isChecked={isChecked} disabled={isDisabled} />;
      default:
        return null;
    }
  }, [item, isChecked, selectableType, isDisabled]);

  const renderImage = useCallback(() => {
    if (!item.image) return null;

    if (item.alliance)
      return (
        <AllianceImageContainer>
          <AllianceImageContent>
            <img alt="" src={item.image} />
          </AllianceImageContent>
        </AllianceImageContainer>
      );

    return (
      <ImageContainer>
        <img alt="" src={item.image} />
      </ImageContainer>
    );
  }, [item]);

  const renderEntityTypeImage = useCallback(() => {
    if (!item.entityTypeImage) return null;

    return <EntityTypeImageContainer>{item.entityTypeImage}</EntityTypeImageContainer>;
  }, [item]);

  const renderSpecificItems = (otherProps: SpecificProps) => (
    <Specific>
      <Specific.Item>
        <Specific.ItemIcon src="/corporate/images/specific-routes/clock.svg" />
        {otherProps.specificFlightAnticipationDays || otherProps.dateHotel || otherProps.dateVehicle
          ? `${otherProps.specificFlightAnticipationDays || otherProps.dateHotel || otherProps.dateVehicle} ${t(
              'rule.restriction.anticipationSuffix',
            )}`
          : t('rule.restriction.withoutRestriction')}
      </Specific.Item>
      <Specific.Item>
        <Specific.ItemIcon src="/corporate/images/specific-routes/money-ico.svg" />
        {otherProps.maxPrice ? formattedPrice(otherProps.maxPrice) : t('rule.restriction.withoutRestriction')}
      </Specific.Item>
      {!otherProps.destinationPoint && typeof otherProps.pickup === 'undefined' && (
        <Specific.Item>
          <Specific.ItemIcon src="/corporate/images/specific-routes/arrow-up.svg" />
          {otherProps.specificFlightFlexibility
            ? `${otherProps.specificFlightFlexibility}%`
            : t('rule.restriction.withoutRestriction')}
        </Specific.Item>
      )}
    </Specific>
  );

  const renderGroupOfBadges = () => {
    if (!item.userGroupId && item.badges) {
      return (
        <GroupBadges
          lineGroup={lineGroup}
          itemsBadges={item.badges.map((badge) =>
            !badge.lastName
              ? {
                  id: badge.id,
                  name: badge.name,
                }
              : {
                  id: badge.id,
                  name: badge.name,
                  lastName: badge.lastName,
                },
          )}
        />
      );
    }

    if (item.userGroupName !== undefined) {
      const nonGroup = item.userGroupName === '';
      const badgeText = nonGroup ? t('group.badge.nonGroup') : item.userGroupName;
      return item.userGroupName === '' && item.disabled ? (
        <>
          <Tooltip content={t('group.badge.groupWithoutPolicies')} event="hover">
            <Badge style={{ backgroundColor: '#ff7600', color: '#fff', fontWeight: '500' }}>!</Badge>&nbsp;
          </Tooltip>
          <Badge outlineBg={nonGroup}>{badgeText}</Badge>
        </>
      ) : (
        <Badge outlineBg={nonGroup}>{badgeText}</Badge>
      );
    }

    if (item.badge && !item.userGroupId) {
      return <Badge>{item.badge.name !== '' ? item.badge.name : t('rule.groups.nonRule')}</Badge>;
    }
  };

  return (
    <Container onClick={() => !isDisabled && onSelect(item)} $disabled={isDisabled}>
      <Row>
        {renderEntityTypeImage()}
        <InfoContainer>
          <ContentContainer>
            {renderImage()}
            <NameContainer>
              {item.detail && <Detail>{item.detail}</Detail>}
              <Name $disabled={isDisabled}>
                {item.name} {item.lastName}
                {isFavorite && <StarOutlined />}
              </Name>
            </NameContainer>
          </ContentContainer>
          {item.email && <SubItem>{item.email}</SubItem>}
          <BadgeContainer>
            {subItems && item.currency ? renderSpecificItems(item.otherProps) : renderGroupOfBadges()}
          </BadgeContainer>
        </InfoContainer>
        {renderSelectType()}
      </Row>
    </Container>
  );
};

export default Item;
